import React from "react";
import { Chip, Stack } from "@mui/material";
import Flag from './Flag';
import VisibilityIcon from '@mui/icons-material/Groups';
import AccessTimeIcon from '@mui/icons-material/Schedule';
import CurrencyGenerator from "../services/CurrencyGenerator";
import { countries } from "../services/Countries";
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { MobileTooltip } from "./Tooltip";

function ItemViewChips({
    countriesDisabled,
    sellersDisabled,
    updateUser,
    item,
    sort,
    lightweight,
    isUser,
    blockedSellers,
    blockedCountries
}) {
    let dateOptions = { month: 'short', day: '2-digit', hour: "numeric", minute: "numeric", hourCycle: "h12" };
    let price =
        'currentBidPrice' in item ?
            <>
                {CurrencyGenerator.getCurrencyString(item.currentBidPrice?.currency, item.currentBidPrice?.value)}
                {' '}
                {'bidCount' in item && `[${item.bidCount} bid${item.bidCount === 1 ? '' : 's'}]\n`}
            </> : null
    let buyItNowPrice = <>
        {'price' in item ?
            <>
                {CurrencyGenerator.getCurrencyString(item.price?.currency, item.price?.value)}
            </>
            :
            null}
    </>;

    let bestOffer = item.buyingOptions?.includes?.("BEST_OFFER");

    let startTime;
    if ('itemEndDate' in item) {
        let endDate = new Date(Date.parse(item.itemEndDate));
        startTime = getDifferenceDateString(new Date(Date.now()), endDate);
    } else {
        let startDate = new Date(Date.parse(item.itemCreationDate));
        startTime = startDate.toLocaleString('en-US', dateOptions);
    }
    let seller = `${item.seller?.username ?? "-"} (${item.seller?.feedbackScore ?? "-"}) ${item.seller?.feedbackPercentage ?? "-"}%`;

    const isZeroScore = item.seller?.feedbackScore < 20 || item.seller?.feedbackPercentage < 90;

    function getDifferenceDateString(startDate, endDate) {
        if (endDate - startDate < 0) {
            return 'Ended';
        }
        let difference = endDate - startDate;
        let days = parseInt(difference / (1000 * 60 * 60 * 24));
        const hours = parseInt(difference / (1000 * 60 * 60) % 24);
        endDate = endDate.getTime();
        startDate = startDate.getTime();
        const minutes = parseInt((endDate - startDate) / (1000 * 60) % 60);
        const seconds = parseInt((endDate - startDate) / (1000) % 60);
        let output = '';
        if (days > 0) {
            output += days + "d ";
        }
        if (hours > 0) {
            output += hours + "h ";
        }
        if (minutes > 0) {
            output += minutes + "m ";
        }
        if (days === 0 && hours === 0 && minutes === 0 && seconds >= 0) {
            output += seconds + "s";
        }
        return output;
    }

    function addToBlockedSellers(seller) {
        updateUser({ blockedSellers: seller }, null, `'${seller}' added to blocked sellers`, null, true, "Are you sure you want to block '" + seller + "'?", "Block", "Block seller", seller);
    }

    function addToBlockedCountries(country) {
        updateUser({ blockedCountries: country }, null, `'${countries[country]}' added to blocked countries`, null, true, "Are you sure you want to block '" + countries[country] + "'?", "Block", "Block country", country, true);
    }

    const DateChip = <Chip className={updateUser ? null : 'ImageChip'} icon={<AccessTimeIcon sx={{ fontSize: '18px' }} />}
        sx={{ fontSize: '0.75em' }} label={startTime} />;

    const auctionWithBin = item.buyingOptions?.includes?.("AUCTION") && item.buyingOptions?.includes?.("FIXED_PRICE");

    if (lightweight) {
        return <div className="ListingOverlaySummary">
            <span>{price ?? buyItNowPrice}</span>
            {item.itemLocation?.country && <span className="ListingCountryTag"><Flag code={item.itemLocation.country} height="14" /></span>}
            <span>{seller}</span>
            <span>{startTime}</span>
        </div>;
    }

    const allowBlockCountry = !countriesDisabled && isUser && !blockedCountries.includes(item.itemLocation?.country);
    const allowBlockSeller = !sellersDisabled && isUser && !blockedSellers.includes(item.seller?.username);
    return (<>
        <Stack direction="row" spacing={updateUser ? 0.5 : 0.25}>
            {(sort === 'EndTimeSoonest') &&
                DateChip
            }
            <Chip className={updateUser ? null : 'ImageChip'} sx={{ fontSize: '0.75em' }} label={<>
                <Stack alignItems={'center'} gap={0.5} direction={'row'}>
                    {price ?? buyItNowPrice} {bestOffer && !auctionWithBin &&
                        <LocalOfferIcon color={'primary'} sx={{ fontSize: '18px' }} />}
                </Stack>
            </>} />

            {auctionWithBin &&
                <Chip className={updateUser ? null : 'ImageChip'} sx={{ fontSize: '0.75em' }} label={
                    <Stack alignItems={'center'} gap={0.5} direction={'row'}>
                        {buyItNowPrice} {bestOffer &&
                            <LocalOfferIcon color={'primary'} sx={{ fontSize: '18px' }} />}
                    </Stack>} />
            }
            {item.itemLocation?.country &&
                <MobileTooltip enterDelay={500} title={allowBlockCountry ? "Block country" : ""}>
                    <Chip className={updateUser ? null : 'ImageChip'}
                        sx={{ fontSize: '0.75em' }}
                        label={<Flag code={item.itemLocation?.country} height={"14"} />}
                        onClick={allowBlockCountry ? () => {
                            addToBlockedCountries(item.itemLocation?.country)
                        } : null} />
                </MobileTooltip>
            }
            <MobileTooltip enterDelay={500} title={allowBlockSeller ? "Block seller" : ""}>
                <Chip color={isZeroScore ? 'primary' : undefined}
                    className={updateUser || isZeroScore ? null : 'ImageChip'} sx={{ fontSize: '0.75em' }}
                    label={seller}
                    onClick={allowBlockSeller ? () => {
                        addToBlockedSellers(item.seller?.username)
                    } : null} />
            </MobileTooltip>
            {(sort !== 'EndTimeSoonest') &&
                DateChip
            }
            <span style={{ color: "white", fontSize: "1px" }}>&nbsp;</span>
        </Stack>
    </>
    );

}

export default React.memo(ItemViewChips);
